import { useCallback, useEffect, useState } from 'react';

/**
 * Le theme.
 *
 * Trois etats, pas deux : `system` est le defaut et signifie "je n'ai rien
 * choisi". Le distinguer de `dark` compte — un joueur qui bascule son
 * telephone en mode nuit le soir veut que le jeu suive, et un jeu qui a
 * memorise `dark` de force ne suivrait plus.
 *
 * Le stockage est un confort, pas un etat de jeu : en navigation privee il
 * peut lever, et le jeu doit s'en moquer.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';

const KEY = 'guess:theme';

function read(): ThemeChoice {
  try {
    const saved = window.localStorage.getItem(KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* site data bloque : on retombe sur le theme systeme */
  }
  return 'system';
}

function write(choice: ThemeChoice): void {
  try {
    if (choice === 'system') window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, choice);
  } catch {
    /* rien a faire : le theme reste valable pour cette session */
  }
}

export function useTheme(): { choice: ThemeChoice; cycle: () => void } {
  const [choice, setChoice] = useState<ThemeChoice>(read);

  useEffect(() => {
    const root = document.documentElement;
    // Retirer l'attribut, plutot que d'y ecrire `system`, est ce qui rend la
    // main a `prefers-color-scheme` : le CSS n'a alors aucun cas a traiter.
    if (choice === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', choice);
    write(choice);
  }, [choice]);

  const cycle = useCallback(() => {
    setChoice((current) =>
      current === 'system' ? 'dark' : current === 'dark' ? 'light' : 'system',
    );
  }, []);

  return { choice, cycle };
}
